import { format } from 'date-fns';
import i18n from '../i18n';

export const parseDate = (date: string | Date): Date | null => {
  if (!date) return null;
  
  try {
    if (typeof date === 'string') {
      // For both ISO format and PostgreSQL timestamp format, create a new Date directly
      return new Date(date);
    }
    return date;
  } catch (error) {
    console.error('Error parsing date:', error, date);
    return null;
  }
};

export const formatDate = (date: string | Date, formatStr?: string): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return i18n.t('common.error', 'Invalid date');
  
  try {
    // Use format string from translations if not provided
    const defaultFormat = i18n.t('dateTime.dateFormat', 'MMM d, yyyy');
    return format(parsedDate, formatStr || defaultFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return i18n.t('common.error', 'Invalid date');
  }
};

export const formatDateTime = (date: string | Date, options?: { timeZone?: string, format?: string }): string => {
  try {
    // Default to Myanmar timezone (UTC+06:30) if not specified
    const timeZone = options?.timeZone || 'Asia/Yangon';
    const parsedDate = parseDate(date);
    if (!parsedDate) return i18n.t('common.error', 'Invalid date');
    
    if (options?.format) {
      return format(parsedDate, options.format);
    }
    
    // Get current language
    const language = i18n.language || 'en';
    
    // Directly use new Date() and toLocaleString with timezone support
    return parsedDate.toLocaleString(language, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return i18n.t('common.error', 'Invalid date');
  }
};

// Helper function to format dates for API requests with Myanmar timezone
export const formatDateForApi = (date: Date | string, isStartDate?: boolean): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';
  
  // Format as ISO string with +06:30 timezone offset for Myanmar
  // This ensures correct date ranges in the local timezone
  // If it's an end date, set to end of day (23:59:59)
  if (isStartDate === false) {
    parsedDate.setHours(23, 59, 59, 999);
  } else if (isStartDate === true) {
    parsedDate.setHours(0, 0, 0, 0);
  }
  
  return parsedDate.toISOString().replace('Z', '+06:30');
};

// Helper function to format date ranges for API requests
export const formatDateRangeForApi = (startDate: Date | string, endDate: Date | string): { start: string, end: string } => {
  const start = parseDate(startDate);
  let end = parseDate(endDate);
  
  if (!start || !end) {
    return { start: '', end: '' };
  }
  
  // Set the end date to end of day (23:59:59)
  if (end) {
    end.setHours(23, 59, 59, 999);
  }
  
  return {
    start: start.toISOString().replace('Z', '+06:30'),
    end: end.toISOString().replace('Z', '+06:30')
  };
};

// Helper function to format date ranges for display
export const formatDateRange = (startDate: Date | string, endDate: Date | string): string => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  
  if (!start || !end) {
    return i18n.t('common.error', 'Invalid date range');
  }
  
  const startFormatted = formatDate(start);
  const endFormatted = formatDate(end);
  
  return `${startFormatted} - ${endFormatted}`;
};